/** Extrae número de portal al final o tras coma (ES). */
function extractNumeroFromCalle(text) {
  const t = String(text || "").trim();
  if (!t) return { calle: "", numero: "" };
  let m = t.match(/[,\s]+(?:n[ºo°.]?\s*)?(\d{1,5}[a-zA-Z]?(?:\s*[-/]\s*\d{1,5}[a-zA-Z]?)?)\s*$/i);
  if (m && !/^\d{5}$/.test(m[1])) {
    return {
      calle: t.replace(new RegExp(`[,\\s]*(?:n[ºo°.]?\\s*)?${m[1]}\\s*$`, "i"), "").trim(),
      numero: m[1],
    };
  }
  m = t.match(/\s+(\d{1,4}[a-zA-Z]?)\s*$/i);
  if (m && !/^\d{5}$/.test(m[1])) {
    return {
      calle: t.replace(new RegExp(`\\s+${m[1]}\\s*$`, "i"), "").trim(),
      numero: m[1],
    };
  }
  return { calle: t, numero: "" };
}

function norm(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/** Prefijos habituales (Google / ES / valenciano) → nombre canónico en aux_tipo_via. */
const PREFIJOS_TIPO_VIA = [
  { re: /^carrer(?:\s+de(?:\s+la|\s+les|\s+l'|\s+el|\s+dels|\s+les)?)?\s+/i, canon: "Calle" },
  { re: /^calle(?:\s+de(?:\s+la|\s+los|\s+las|\s+el|\s+lo)?)?\s+/i, canon: "Calle" },
  { re: /^c\/\s+/i, canon: "Calle" },
  { re: /^cl\.?\s+/i, canon: "Calle" },
  { re: /^avinguda(?:\s+de(?:\s+la|\s+les|\s+l'|\s+el)?)?\s+/i, canon: "Avenida" },
  { re: /^avenida(?:\s+de(?:\s+la|\s+los|\s+las|\s+el)?)?\s+/i, canon: "Avenida" },
  { re: /^avda\.?\s+/i, canon: "Avenida" },
  { re: /^av\.?\s+/i, canon: "Avenida" },
  { re: /^passeig(?:\s+de(?:\s+la|\s+les|\s+l'|\s+el)?)?\s+/i, canon: "Paseo" },
  { re: /^paseo(?:\s+de(?:\s+la|\s+los|\s+las|\s+el)?)?\s+/i, canon: "Paseo" },
  { re: /^p\.?\s*º\.?\s+/i, canon: "Paseo" },
  { re: /^plaça(?:\s+de(?:\s+la|\s+les|\s+l'|\s+el)?)?\s+/i, canon: "Plaza" },
  { re: /^plaza(?:\s+de(?:\s+la|\s+los|\s+las|\s+el)?)?\s+/i, canon: "Plaza" },
  { re: /^pl\.?\s+/i, canon: "Plaza" },
  { re: /^camí(?:\s+de(?:\s+la|\s+les|\s+l'|\s+el)?)?\s+/i, canon: "Camino" },
  { re: /^camino(?:\s+de(?:\s+la|\s+los|\s+las|\s+el)?)?\s+/i, canon: "Camino" },
  { re: /^carretera(?:\s+de(?:\s+la|\s+los|\s+las|\s+el)?)?\s+/i, canon: "Carretera" },
  { re: /^cr\.?\s+/i, canon: "Carretera" },
  { re: /^urbanizaci[oó]n(?:\s+de(?:\s+la|\s+los|\s+las|\s+el)?)?\s+/i, canon: "Urbanización" },
  { re: /^urb\.?\s+/i, canon: "Urbanización" },
  { re: /^partida(?:\s+de(?:\s+la|\s+les|\s+l'|\s+el)?)?\s+/i, canon: "Partida" },
  { re: /^pol[ií]gono(?:\s+\d+)?\s+/i, canon: "Polígono" },
  { re: /^pg\.?\s+/i, canon: "Polígono" },
];

function resolverIdTipoVia(nombre, tiposVia = []) {
  if (!nombre) return "";
  const n = norm(nombre);
  const hit = tiposVia.find((t) => norm(t.nombre) === n);
  if (hit?.id) return String(hit.id);
  // Alias habituales ES / CA / abreviaturas → catálogo CRM
  const ALIAS = {
    carrer: ["calle", "carrer"],
    calle: ["calle", "carrer"],
    "c/": ["calle"],
    cl: ["calle"],
    avinguda: ["avenida", "avinguda"],
    avenida: ["avenida", "avinguda"],
    avda: ["avenida"],
    av: ["avenida"],
    passeig: ["paseo", "passeig"],
    paseo: ["paseo", "passeig"],
    placa: ["plaza", "plaça", "placa"],
    plaza: ["plaza", "plaça", "placa"],
    cami: ["camino", "camí", "cami"],
    camino: ["camino", "camí", "cami"],
  };
  const aliases = ALIAS[n] || [n];
  for (const a of aliases) {
    const h2 = tiposVia.find((t) => norm(t.nombre) === a);
    if (h2?.id) return String(h2.id);
  }
  // Contiene (p. ej. "Calle / Carrer")
  for (const a of aliases) {
    const h3 = tiposVia.find((t) => {
      const tn = norm(t.nombre);
      return tn === a || tn.includes(a) || a.includes(tn);
    });
    if (h3?.id) return String(h3.id);
  }
  return "";
}

/**
 * Separa tipo de vía, nombre de vía y número a partir de una línea de calle (Google Places, etc.).
 * @returns {{ tipoViaId: string, tipoViaCanon: string, nombreVia: string, numero: string }}
 */
export function parseTipoViaCalle(rawLine, tiposVia = []) {
  const { calle: sinNumero, numero: numExtraido } = extractNumeroFromCalle(rawLine);
  let calle = sinNumero;
  let numero = numExtraido;
  let tipoViaId = "";
  let tipoViaCanon = "";

  const sorted = [...(tiposVia || [])].sort(
    (a, b) => String(b.nombre || "").length - String(a.nombre || "").length,
  );
  for (const t of sorted) {
    const name = String(t.nombre || "").trim();
    if (!name) continue;
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`^${escaped}(?:\\s+de(?:\\s+la|\\s+los|\\s+las|\\s+el|\\s+les|\\s+l'|\\s+dels)?)?\\s+`, "i");
    if (re.test(calle)) {
      tipoViaId = String(t.id);
      tipoViaCanon = name;
      calle = calle.replace(re, "").trim();
      break;
    }
    const reSimple = new RegExp(`^${escaped}\\b\\s*`, "i");
    if (reSimple.test(calle)) {
      tipoViaId = String(t.id);
      tipoViaCanon = name;
      calle = calle.replace(reSimple, "").trim();
      break;
    }
  }

  if (!tipoViaId) {
    for (const { re, canon } of PREFIJOS_TIPO_VIA) {
      if (re.test(calle)) {
        tipoViaCanon = canon;
        tipoViaId = resolverIdTipoVia(canon, tiposVia);
        calle = calle.replace(re, "").trim();
        break;
      }
    }
  }

  return {
    tipoViaId,
    tipoViaCanon,
    nombreVia: calle,
    numero,
  };
}
